import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  Mail, 
  Lock, 
  Shield, 
  Eye, 
  EyeOff, 
  Building2,
  Key,
  Loader2,
  LayoutDashboard,
  Users,
  Settings,
  BarChart3,
  Boxes,
  Activity,
} from 'lucide-react';
import { Button, Input, Card, CardBody } from '../components/ui';
import { useAuthStore } from '../stores/auth.store';
import { api } from '../lib/api';

// Admin login always targets tenant-demo-001 (SA Soluções)
const ADMIN_TENANT_ID = 'tenant-demo-001';
const ADMIN_TENANT_NAME = 'SA Soluções';

const loginSchema = z.object({
  email: z.string().email({ message: 'Email inválido' }),
  password: z.string().min(6, 'Senha deve ter no mínimo 6 caracteres'),
});

type LoginForm = z.infer<typeof loginSchema>;

const adminFeatures = [
  {
    icon: LayoutDashboard,
    title: 'Dashboard Completo',
    description: 'Visão geral de todos os tenants e métricas da plataforma',
  },
  {
    icon: Building2,
    title: 'Gestão de Tenants',
    description: 'Crie e gerencie tenants com configurações individuais',
  },
  {
    icon: Users,
    title: 'Usuários Globais',
    description: 'Administre usuários de qualquer tenant da plataforma',
  },
  {
    icon: Boxes,
    title: 'Módulos da Plataforma',
    description: 'Ative e configure módulos por tenant e tier',
  },
  {
    icon: BarChart3,
    title: 'Receita e Taxas',
    description: 'Monitore receita, taxas e custos operacionais',
  },
  {
    icon: Activity,
    title: 'Monitoramento',
    description: 'Saúde do sistema, alertas e logs de auditoria',
  },
];

export function AdminLogin() {
  const navigate = useNavigate();
  const { login, isLoading, error, clearError } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [verifying2FA, setVerifying2FA] = useState(false);
  const [twoFAError, setTwoFAError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginForm) => {
    try {
      const result = await login(data.email, data.password, ADMIN_TENANT_ID);
      
      if (result?.requires2FA && result.tempToken) {
        setRequires2FA(true);
        setTempToken(result.tempToken);
        return;
      }
      
      navigate('/');
    } catch {
      // Error is handled by store
    }
  };

  const handleVerify2FA = async () => {
    if (totpCode.length !== 6) return;
    
    setVerifying2FA(true);
    setTwoFAError('');
    
    try {
      const response = await api.post('/auth/verify-2fa', {
        tempToken,
        code: totpCode,
      });
      
      const { accessToken, refreshToken, user } = response.data;
      useAuthStore.getState().setAuth(accessToken, refreshToken, user);
      navigate('/');
    } catch (err: any) {
      setTwoFAError(err.response?.data?.message || 'Código inválido. Tente novamente.');
      setTotpCode('');
    } finally {
      setVerifying2FA(false);
    }
  };

  return (
    <div className="min-h-screen flex relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-amber-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-orange-500/10 rounded-full blur-3xl" />
        <div className="grid-bg absolute inset-0 opacity-10" />
      </div>

      {/* Left Side - Admin Features */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 p-8 xl:p-16 flex-col justify-center relative">
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl"
        >
          {/* Logo & Title */}
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-amber-400">Painel Administrativo</h1>
              <p className="text-surface-400">Acesso restrito à administração</p>
            </div>
          </div>

          {/* Main Headline */}
          <h2 className="text-4xl xl:text-5xl font-bold text-surface-100 mb-6 leading-tight">
            Controle total da{' '}
            <span className="text-amber-400">plataforma</span>
          </h2>

          <p className="text-lg text-surface-400 mb-10">
            Gerencie tenants, módulos, taxas, compliance e todos os aspectos 
            operacionais da plataforma financeira.
          </p>

          {/* Features Grid */}
          <div className="grid grid-cols-2 gap-4 mb-10">
            {adminFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 * index }}
                className="flex items-start gap-3 p-4 rounded-xl bg-surface-900/50 border border-surface-800/50 hover:border-amber-500/30 transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-surface-200 text-sm">{feature.title}</h3>
                  <p className="text-xs text-surface-500 mt-0.5">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-6 lg:p-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 mb-4 shadow-lg">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-amber-400">Painel Admin</h1>
            <p className="text-surface-400 mt-2">Acesso restrito</p>
          </div>

          {/* Login Card */}
          <Card variant="glass" className="backdrop-blur-xl">
            <CardBody className="p-8">
              {!requires2FA ? (
                <>
                  <div className="mb-6">
                    <h2 className="text-2xl font-bold text-surface-100">
                      Acesso Administrativo
                    </h2>
                    <p className="text-surface-400 mt-1">
                      Entre com suas credenciais de administrador
                    </p>
                  </div>

                  {/* Fixed Tenant Display */}
                  <div className="mb-5 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-amber-400" />
                    <div>
                      <p className="text-xs text-surface-400">Empresa</p>
                      <p className="text-sm font-semibold text-surface-200">{ADMIN_TENANT_NAME}</p>
                    </div>
                  </div>

                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm"
                    >
                      {error}
                      <button
                        onClick={clearError}
                        className="float-right text-red-300 hover:text-red-200"
                      >
                        ×
                      </button>
                    </motion.div>
                  )}

                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                    <Input
                      label="Email"
                      type="email"
                      placeholder="admin@empresa.com"
                      leftIcon={<Mail className="w-4 h-4" />}
                      error={errors.email?.message}
                      {...register('email')}
                    />

                    <Input
                      label="Senha"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      autoComplete="current-password"
                      leftIcon={<Lock className="w-4 h-4" />}
                      rightIcon={
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="text-surface-500 hover:text-surface-300"
                        >
                          {showPassword ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      }
                      error={errors.password?.message}
                      {...register('password')}
                    />

                    <Button
                      type="submit"
                      variant="primary"
                      className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
                      isLoading={isLoading}
                    >
                      Entrar
                    </Button>
                  </form>
                </>
              ) : (
                /* 2FA Verification Step */
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-6"
                >
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
                      <Key className="w-8 h-8 text-amber-400" />
                    </div>
                    <h2 className="text-xl font-bold text-surface-100">
                      Verificação em Duas Etapas
                    </h2>
                    <p className="text-surface-400 mt-2 text-sm">
                      Digite o código de 6 dígitos do seu aplicativo autenticador
                    </p>
                  </div>

                  {twoFAError && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm"
                    >
                      {twoFAError}
                    </motion.div>
                  )}

                  <div className="space-y-4">
                    <Input
                      label="Código de Verificação"
                      type="text"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      maxLength={6}
                      className="text-center text-2xl tracking-widest"
                      autoFocus
                    />

                    <Button
                      onClick={handleVerify2FA}
                      variant="primary"
                      className="w-full bg-gradient-to-r from-amber-500 to-orange-600"
                      disabled={totpCode.length !== 6 || verifying2FA}
                    >
                      {verifying2FA ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Verificando...
                        </>
                      ) : (
                        'Verificar'
                      )}
                    </Button>
                  </div>

                  <button
                    onClick={() => {
                      setRequires2FA(false);
                      setTotpCode('');
                      setTwoFAError('');
                    }}
                    className="w-full text-sm text-surface-400 hover:text-surface-300"
                  >
                    ← Voltar para login
                  </button>
                </motion.div>
              )}
            </CardBody>
          </Card>

          {/* Footer */}
          <p className="text-center text-surface-600 text-sm mt-8">
            © 2024 {ADMIN_TENANT_NAME}. Painel Administrativo.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
